import React, { useState } from 'react';
import { Navbar, Footer } from '../components/Navbar';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { api, formatApiError } from '../lib/api';
import { usePageSEO } from '../lib/usePageSEO';

/**
 * Phase 1 placeholder — accepts a request and stores it in the
 * `assistance_requests` collection for an admin to review. Phase 3 will
 * generate the actual promo code on approval and email it.
 */
export default function RequestAssistance() {
  usePageSEO({
    title: 'Request Memorial Assistance — MyLeaf Legacy',
    description: 'Hardship support for veterans, first responders, and families in financial need. Tell us about your loved one — we offer free and discounted memorials for those who qualify.',
    path: '/request-assistance',
  });
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [category, setCategory] = useState('veteran');
  const [story, setStory] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/assistance/requests', {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim() || null,
        category,
        story: story.trim().slice(0, 1500),
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="ml-container py-16 sm:py-24 max-w-3xl">
        <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--accent))]" data-testid="assistance-eyebrow">
          Community support
        </p>
        <h1 className="font-serif text-4xl sm:text-5xl text-[hsl(var(--primary))] mt-2" data-testid="assistance-title">
          Request assistance pricing
        </h1>
        <p className="mt-4 text-[hsl(var(--primary)/0.78)] leading-relaxed max-w-2xl">
          Reduced pricing is available for veterans, families fighting cancer, and parents
          carrying their children through hardship. Tell us a little about your situation —
          we'll reply within 1–2 business days with a private discount code by email.
        </p>

        {submitted ? (
          <div className="mt-10 rounded-2xl border border-[hsl(var(--accent)/0.5)] bg-[hsl(var(--primary)/0.04)] px-7 py-8" data-testid="assistance-success">
            <h2 className="font-serif text-2xl text-[hsl(var(--primary))]">Thank you for reaching out.</h2>
            <p className="text-sm text-[hsl(var(--primary)/0.78)] mt-2 max-w-2xl">
              Your request is now with our team. We read every single one. You'll hear back from us by email — usually within 1–2 business days.
            </p>
            <Link to="/" className="ml-btn-secondary mt-6 inline-flex" data-testid="assistance-return-home">Return home</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 space-y-5 wizard-readable">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="ml-label">Your name</label>
                <input required value={name} onChange={(e) => setName(e.target.value)} className="ml-input" data-testid="assistance-name" />
              </div>
              <div>
                <label className="ml-label">Phone (optional)</label>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} className="ml-input" data-testid="assistance-phone" />
              </div>
            </div>
            <div>
              <label className="ml-label">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="ml-input" data-testid="assistance-email" />
            </div>
            <div>
              <label className="ml-label">Category</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
                {[
                  { id: 'veteran', label: 'Veteran' },
                  { id: 'cancer', label: 'Cancer family' },
                  { id: 'hardship', label: 'Family hardship' },
                  { id: 'other', label: 'Other' },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setCategory(opt.id)}
                    data-testid={`assistance-category-${opt.id}`}
                    className={`rounded-sm border px-3 py-2 text-[12px] font-semibold tracking-wide transition ${
                      category === opt.id
                        ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-[#F4E5C2]'
                        : 'border-[hsl(var(--primary))]/25 text-[hsl(var(--primary))] hover:border-[hsl(var(--primary))]'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="ml-label">A little about your situation</label>
              <textarea
                value={story}
                onChange={(e) => setStory(e.target.value.slice(0, 1500))}
                rows={6}
                placeholder="Whatever you'd like us to know — kept private to our team."
                className="ml-input-boxed"
                data-testid="assistance-story"
              />
              <p className="text-xs text-[hsl(var(--primary)/0.5)] mt-1">{story.length}/1500</p>
            </div>
            <button type="submit" disabled={loading} className="ml-btn-primary w-full !py-3.5" data-testid="assistance-submit">
              {loading ? 'Sending…' : 'Send request'}
            </button>
          </form>
        )}
      </main>
      <Footer />
    </div>
  );
}
